import { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import validator from "validator";
import { Eye, EyeOff, Loader2, X } from "lucide-react";
import { fetchCompanyDetails } from "../../../../../api/action/company.jsx";
import { googleSignin, logout, OtpLogin, sendOtp, signin } from "../../../../../api/action/user.jsx";
import * as types from "../../../../../api/action/types.jsx";
import { getDefaultCountryCode } from "../../../../../config/locationConfig.js";
import keys from "../../../../../config/keys.jsx";
import {
    clearConsoleHandoffData,
    clearLoginTransition,
    finalizeAuthenticatedSession,
    getConsoleHandoffData,
    isLoginTransitionActive,
    setLoginTransition,
    stageAuthenticatedSession,
    storeConsoleHandoffData,
} from "../../../../../utils/loginTransition.jsx";
import { encodeBase64 } from "../../../../loginOrRegister/encryptPassword.jsx";
import { useAuthSession } from "../../../../../auth/useAuthSession.js";
import { SESSION_EXPIRED_MESSAGE } from "../../../../../auth/sessionMessages.js";
import { GOOGLE_SIGNIN_CANCELLED, isNativeMobileApp, signInWithGoogleNative } from "../../../../../utils/googleAuth.js";
import GoogleAuthButton from "../../../../../components/GoogleAuthButton.jsx";

const localConsoleDomain = keys?.localConsoleDomain || "";
const domain = keys?.domain || "";
const GOOGLE_CLIENT_ID =
    process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

const statusMessageClasses = {
    error: "border-red-200 bg-red-50 text-red-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    info: "border-blue-200 bg-blue-50 text-blue-700",
};

const getFlexRoleLabel = (role) => {
    switch (role) {
        case "CUSTOMER":
            return "Guest";
        case "EMPLOYEE":
            return "Employee";
        case "ADMIN":
            return "Admin";
        case "INSTRUCTOR":
            return "Instructor Login";
        case "SUPER_ADMIN":
            return "Super Admin Login";
        default:
            return role;
    }
};

const StatusMessage = ({ severity = "error", children, onClose, className = "" }) => {
    if (!children) return null;

    return (
        <div
            role={severity === "error" ? "alert" : "status"}
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left text-sm ${statusMessageClasses[severity] || statusMessageClasses.info} ${className}`}
        >
            <span className="flex-1">{children}</span>
            {onClose && (
                <button
                    type="button"
                    aria-label="Close message"
                    onClick={onClose}
                    className="rounded-md p-0.5 opacity-70 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            )}
        </div>
    );
};

const normalizeUrl = (url) => String(url || "").trim().replace(/\/+$/, "");

const getConsoleReturnUrl = () => {
    const returnUrl = new URLSearchParams(window.location.search || "").get("returnUrl") || "";
    if (!returnUrl.startsWith("/console/") || returnUrl.startsWith("//")) return "";
    return returnUrl;
};

const appendConsoleHandoffParams = (url, encryptedEmail, encryptedPassword, companyCode) => {
    const separator = String(url || "").includes("?") ? "&" : "?";
    return `${url}${separator}email=${encodeURIComponent(encryptedEmail)}&password=${encodeURIComponent(encryptedPassword || "")}&companyCode=${encodeURIComponent(companyCode || "")}`;
};

const getConsoleBaseUrl = (companyUrl = "", prefix = "") => {
    const configuredCompanyUrl = normalizeUrl(companyUrl);
    if (configuredCompanyUrl) {
        return configuredCompanyUrl.includes(":3000")
            ? configuredCompanyUrl.replace(":3000", ":4000")
            : configuredCompanyUrl;
    }

    const { origin = "", protocol = "", hostname = "", port = "" } = window.location || {};
    if (protocol === "http:" && port === "3000") {
        return `http://${hostname}:4000`;
    }

    if (origin.includes(":3000")) {
        return origin.replace(":3000", ":4000");
    }

    if (localConsoleDomain) {
        return `http://${prefix ? `${prefix}.` : ""}${localConsoleDomain}`;
    }

    if (domain) {
        return `https://${prefix || "www"}.${domain}`;
    }

    return normalizeUrl(origin);
};

const normalizeDomainType = (domainType) => String(domainType || "").trim().toUpperCase();

const FlexLogin = ({ templateDomainType }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otpCountdown, setOtpCountdown] = useState(0);
    const [, setGoogleScriptLoaded] = useState(false);
    const googlePromptShownRef = useRef(false);
    const initializeGoogleOneTapRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const authSession = useAuthSession();
    const [state, setState] = useState({ open: false, message: "", severity: "error" });
    const [user, setUser] = useState({ email: "", password: "" });
    const [showEmployeeChoiceModal, setShowEmployeeChoiceModal] = useState(false);
    const [showRoleChoiceModal, setShowRoleChoiceModal] = useState(false);
    const [legalDocumentLinks, setLegalDocumentLinks] = useState([]);
    const legalDocuments = useSelector((state) => state.companyReducer.company?.legalDocuments);

    const companyData = useSelector((state) => state?.companyReducer?.company || {});
    const companyPrimaryId = companyData?.primaryId || '';
    const companyUrl = companyData?.url || '';
    const companyCode = companyData?.primaryId || "";
    const countryCallingCode = getDefaultCountryCode(companyData);
    const activeDomainType = normalizeDomainType(
        templateDomainType
        || companyData?.domain?.type
        || companyData?.domainType
        || localStorage.getItem("domainType")
    );
    const isFlexOfficeDomain = activeDomainType === "FLEX_OFFICE";
    const [showContinueAsModal, setShowContinueAsModal] = useState(false);
    const clearSessionButKeepTheme = () => dispatch(logout({ reason: "fresh-login" }));

    const commitSelectedRole = (role) => {
        const token = localStorage.getItem("token");
        const email = localStorage.getItem("email");
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const selectedUserData = { ...userData, userRoles: [role], userRole: role };

        finalizeAuthenticatedSession({
            token,
            email,
            roles: [role],
            userData: selectedUserData,
        });
        dispatch({
            type: types.SIGNIN_SUCCESS,
            payload: {
                ...selectedUserData,
                data: selectedUserData,
                token,
                email,
                userRoles: [role],
                userRole: role,
                isAuthenticated: true,
                isLoggedIn: true,
            },
        });
    };

    const formatContentSubType = (contentSubType) => {
        if (!contentSubType) return "Legal Document";
        return contentSubType
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const handleLegalDocumentClick = (doc, e) => {
        e.preventDefault();

        if (doc.contentSubType) {
            const policyName = doc.contentSubType.toLowerCase();
            navigate(`/policy/${policyName}`);
        } else {
            navigate(`/policy/${doc.name?.toLowerCase().replace(/\s+/g, '-')}`);
        }
    };

    useEffect(() => {
        if (legalDocuments?.length > 0) {
            const formattedLinks = legalDocuments.map(doc => ({
                name: formatContentSubType(doc.contentSubType) || doc.name || "Legal Document",
                href: "#",
                document: doc
            }));
            setLegalDocumentLinks(formattedLinks);
        }
    }, [legalDocuments]);

    // Handle Google One Tap response with improved error handling
    const handleGoogleCredentialResponse = async (response) => {
        setLoading(true);
        setError("");
        setSuccess("");

        const idToken = response.credential;

        try {
            // Decode and extract email from JWT
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            const googleEmail = payload.email;

            if (!googleEmail) {
                throw new Error("Email not received from Google");
            }

            const signinPayload = {
                email: googleEmail,
                authProvider: "GOOGLE",
                userRoles: ["CUSTOMER"],
                authToken: idToken,
                company: { primaryId: companyPrimaryId },
            };

            // Dispatch Google signin action
            const resultAction = await dispatch(googleSignin(signinPayload));

            // Check for successful response - be flexible with response structure
            const isSuccess =
                resultAction?.type?.includes('SUCCESS') ||
                resultAction?.payload?.status === 200 ||
                resultAction?.status === 200 ||
                resultAction?.data?.status === 200 ||
                resultAction?.payload?.message?.toLowerCase().includes("success") ||
                resultAction?.payload?.message?.toLowerCase().includes("login successful") ||
                resultAction?.message?.toLowerCase().includes("success");

            if (isSuccess) {
                setSuccess("Login successful! Redirecting...");

                // Extract response data from various possible locations
                const responseData = resultAction?.payload?.data ||
                    resultAction?.data ||
                    resultAction?.payload ||
                    resultAction;

                // Handle the successful login immediately
                await handleSuccessfulLogin(responseData, googleEmail);
            } else {
                // Extract error message from various possible locations
                const errorMessage =
                    resultAction?.payload?.message ||
                    resultAction?.error?.message ||
                    resultAction?.message ||
                    resultAction?.payload?.error?.message ||
                    "Google login failed. Please try again.";

                throw new Error(errorMessage);
            }
        } catch (err) {
            setError(err?.message || "Google login error. Please try again.");
            setLoading(false);
        }
    };

    const handleNativeGoogleSignIn = async () => {
        setLoading(true);
        setError("");
        try {
            const idToken = await signInWithGoogleNative();
            await handleGoogleCredentialResponse({ credential: idToken });
        } catch (err) {
            if (err?.message !== GOOGLE_SIGNIN_CANCELLED) {
                setError(err?.message || "Google login error. Please try again.");
            }
            setLoading(false);
        }
    };

    // Load Google script (web only — the native shell uses @capacitor/google-auth,
    // wired to the "Continue with Google" button rendered below instead)
    useEffect(() => {
        if (isNativeMobileApp()) {
            return;
        }
        if (window.google) {
            setGoogleScriptLoaded(true);
            initializeGoogleOneTapRef.current?.();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            setGoogleScriptLoaded(true);
            initializeGoogleOneTapRef.current?.();
        };
        script.onerror = () => {
            setError('Failed to load Google authentication');
        };
        document.head.appendChild(script);

        return () => {
            const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, []);

    // Initialize Google One Tap
    const initializeGoogleOneTap = () => {
        if (!window.google || !window.google.accounts) {
            return;
        }

        try {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleCredentialResponse,
                auto_select: false,
                cancel_on_tap_outside: true,
                context: "signin"
            });

            const googleButtonElement = document.getElementById("googleOneTapButton");
            if (!googleButtonElement) {
                return;
            }

            const measuredWidth = googleButtonElement.getBoundingClientRect().width || googleButtonElement.offsetWidth;
            const viewportFallback = typeof window !== "undefined" ? window.innerWidth - 48 : 360;
            const buttonWidth = Math.min(430, Math.max(280, Math.floor(measuredWidth || viewportFallback || 360)));

            googleButtonElement.innerHTML = "";

            window.google.accounts.id.renderButton(
                googleButtonElement,
                {
                    type: "standard",
                    theme: "outline",
                    size: "large",
                    text: "signin_with",
                    shape: "rectangular",
                    logo_alignment: "left",
                    width: buttonWidth
                }
            );

            // Show Google One Tap prompt if not already logged in
            setTimeout(() => {
                if (!googlePromptShownRef.current && !(authSession.isAuthenticated || authSession.isLoggedIn)) {
                    googlePromptShownRef.current = true;
                    window.google.accounts.id.prompt();
                }
            }, 1000);

        } catch {
            setGoogleScriptLoaded(false);
        }
    };
    initializeGoogleOneTapRef.current = initializeGoogleOneTap;

    // Input handlers
    const handleEmailPasswordChange = useCallback(({ target: { name, value } = {} } = {}) => {
        if (name && value !== undefined) {
            setUser((prevUser) => ({ ...(prevUser || {}), [name]: value }));
            setError("");
            setSuccess("");
        }
    }, []);

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setPhoneNumber(value);
        setError("");
        setSuccess("");
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setOtp(value);
        setError("");
        setSuccess("");
    };

    // Validation
    const validateEmail = (email) => validator.isEmail(email);
    const validatePhone = (phone) => phone.length === 10;

    // Login functions
    const handleEmailLogin = async (e) => {
        e?.preventDefault();

        if (!user.email || !user.password) {
            setError("Email and password are required.");
            return;
        }

        if (!validateEmail(user.email)) {
            setError("Please enter a valid email address.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await dispatch(
                signin({
                    email: user.email,
                    password: user.password,
                    company: { primaryId: companyPrimaryId }
                })
            );

            if (response?.status === 200 || response?.message?.includes('Login successful')) {
                await handleSuccessfulLogin(response, user.email, user.password);
            } else {
                setError(response?.message || "Login failed. Please check your credentials.");
                setLoading(false);
            }
        } catch (err) {
            setError(err?.message || "Login failed. Please try again.");
            setLoading(false);
        }
    };

    const handleSendOtp = async () => {
        if (!validatePhone(phoneNumber)) {
            setError("Please enter a valid 10-digit phone number.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const formattedPhoneNumber = `${phoneNumber}`;

            const response = await dispatch(
                sendOtp({
                    phoneNumber: formattedPhoneNumber,
                    deleted: false,
                    company: { primaryId: companyPrimaryId }
                }, "Phone_otp")
            );

            if (response) {
                setOtpSent(true);
                setOtpCountdown(60);
                setSuccess("OTP sent successfully to your phone");

                const timer = setInterval(() => {
                    setOtpCountdown((prev) => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            }
        } catch (err) {
            setError(err?.message || "Failed to send OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleOtpLogin = async (e) => {
        e?.preventDefault();

        if (!otp || otp.length !== 6) {
            setError("Please enter a valid 6-digit OTP.");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const formattedPhoneNumber = `${phoneNumber}`;

            const response = await dispatch(
                OtpLogin({
                    phoneNumber: formattedPhoneNumber,
                    dynamicAttributes: [{
                        name: "Phone_otp",
                        value: otp
                    }],
                    company: { primaryId: companyPrimaryId }
                }, "Phone_otp")
            );

            if (response?.status === 200 || response?.message?.includes('Login successful')) {
                await handleSuccessfulLogin(response);
            } else {
                setError(response?.message || "OTP verification failed.");
                setLoading(false);
            }
        } catch (err) {
            setError(err?.message || "OTP verification failed. Please try again.");
            setLoading(false);
        }
    };

    const performAdminConsoleRedirect = useCallback(async (userEmail, userPassword) => {
        try {
            const handoffData = getConsoleHandoffData();
            const encryptedPassword = handoffData?.encryptedPassword || encodeBase64(userPassword || "");
            const encryptedEmail = handoffData?.encryptedEmail || encodeBase64(userEmail || "");
            const consolePath = getConsoleReturnUrl() || "/console/manage";
            const dashboardUrl = `${getConsoleBaseUrl(companyUrl)}${consolePath}`;
            const redirectUrl = appendConsoleHandoffParams(dashboardUrl, encryptedEmail, encryptedPassword, companyPrimaryId || companyCode);

            setLoginTransition("console-redirect");

            if (dashboardUrl) {
                window.location.assign(redirectUrl);
            } else {
                setError("Dashboard URL not configured");
                setLoading(false);
            }

        } catch {
            setError("Error opening console. Please try again.");
            setLoading(false);
        }
    }, [companyCode, companyPrimaryId, companyUrl]);

    const handleSuccessfulLogin = async (response, emailOverride = null, passwordOverride = null) => {
        localStorage.removeItem("guestAuthenticated");
        const pendingBooking = sessionStorage.getItem("pendingBooking");
        const loginRedirectUrl = sessionStorage.getItem("loginRedirectUrl");
        sessionStorage.removeItem("guestAuthData");
        sessionStorage.removeItem("guestData");
        sessionStorage.removeItem("guestshoppingCartId");
        sessionStorage.removeItem("encryptedPassword");
        sessionStorage.removeItem("encryptedEmail");
        sessionStorage.removeItem("guestAccountId");
        if (pendingBooking !== null) {
            sessionStorage.setItem("pendingBooking", pendingBooking);
        }

        if (loginRedirectUrl !== null) {
            sessionStorage.setItem("loginRedirectUrl", loginRedirectUrl);
        }

        const responseData = response?.data || response?.payload?.data || response;
        const userRoles = responseData?.userRoles || [];
        const token = responseData?.accessConfirmationToken ||
            responseData?.token ||
            response?.token ||
            response?.payload?.token;
        const userEmail = emailOverride || responseData?.email || user?.email;
        const userPassword = passwordOverride || user?.password;

        try {
            stageAuthenticatedSession({
                token,
                email: userEmail,
                roles: userRoles,
                userData: responseData,
            });
            storeConsoleHandoffData({
                encryptedEmail: encodeBase64(userEmail || ""),
                encryptedPassword: encodeBase64(userPassword || ""),
                companyCode: companyPrimaryId,
            });
        } catch (storageError) {
            setError(storageError?.message || "Unable to prepare login session. Please try again.");
            clearLoginTransition();
            setLoading(false);
            return;
        }

        if (userRoles.length > 1) {
            setLoginTransition("role-selection");
            setShowRoleChoiceModal(true);
            setLoading(false);
            return;
        }

        const primaryRole = userRoles[0];

        if (primaryRole === "CUSTOMER") {
            finalizeAuthenticatedSession({
                token,
                email: userEmail,
                roles: userRoles,
                userData: responseData,
            });

            if (pendingBooking) {
                try {
                    const bookingData = JSON.parse(pendingBooking);
                    navigate('/selected-product-details', {
                        state: bookingData,
                        replace: true
                    });
                } catch {
                    navigate('/flex-home', { replace: true });
                }
            } else {
                navigate('/flex-home', { replace: true });
            }

            setLoading(false);
            return;
        }

        if (primaryRole === "EMPLOYEE") {
            setLoginTransition("employee-choice");
            setShowEmployeeChoiceModal(true);
            setLoading(false);
            return;
        }

        if (["INSTRUCTOR", "ADMIN", "SUPER_ADMIN"].includes(primaryRole)) {
            await performAdminConsoleRedirect(userEmail, userPassword);
            return;
        }

        clearLoginTransition();
        setError("Invalid user role");
        setLoading(false);
    };

    useEffect(() => {
        if (!isFlexOfficeDomain) return;

        const intentionalLogout = localStorage.getItem("intentionalLogout");
        if (intentionalLogout) {
            localStorage.removeItem("intentionalLogout");
            void dispatch(logout({ reason: "intentional-logout" }));

            return;
        }

        const tokenExpired = localStorage.getItem("isTokenExpired");
        if (tokenExpired) {
            setState({
                open: true,
                message: SESSION_EXPIRED_MESSAGE,
                severity: "error",
            });
            localStorage.removeItem("isTokenExpired");
        }
        if (isLoginTransitionActive()) return;

        const isLoggedIn = authSession.isAuthenticated || authSession.isLoggedIn;
        if (!isLoggedIn) return;

        const userRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");

        const hasAdminRole = userRoles.some(r =>
            ["ADMIN", "SUPER_ADMIN", "INSTRUCTOR"].includes(r)
        );
        const hasCustomerRole = userRoles.includes("CUSTOMER");

        if (hasAdminRole && hasCustomerRole) {
            setShowContinueAsModal(true);
            return;
        }
        if (hasAdminRole) {
            const userEmail = localStorage.getItem("email");
            const userPassword = user?.password || "";
            performAdminConsoleRedirect(userEmail, userPassword);
            return;
        }

        navigate("/flex-home", { replace: true });
    }, [authSession.isAuthenticated, authSession.isLoggedIn, isFlexOfficeDomain, navigate, performAdminConsoleRedirect, user?.password]);

    // Fetch company details on mount
    useEffect(() => {
        const fullUrl = window?.location?.href || "";
        const subdomain = fullUrl.split("/").slice(0, 3).join("/");
        if (subdomain) {
            fetchCompanyDetails(subdomain);
        }
    }, []);

    // Modal handlers
    const handleEmployeeChoice = async (choice) => {
        setShowEmployeeChoiceModal(false);

        if (choice === 'console') {
            try {
                const companyString = localStorage.getItem('company');
                if (!companyString) {
                    setError("Company details not found.");
                    return;
                }

                const company = JSON.parse(companyString);
                const prefix = company?.urlPrefix || "";
                const consolePath = getConsoleReturnUrl() || "/console/manage";
                const consoleURL = `${getConsoleBaseUrl(company?.url, prefix)}${consolePath}`;
                const handoffData = getConsoleHandoffData();

                if (!handoffData?.encryptedEmail) {
                    setError("Session expired. Please login again.");
                    return;
                }

                storeConsoleHandoffData({
                    ...handoffData,
                    companyCode,
                });
                const redirectUrl = appendConsoleHandoffParams(consoleURL, handoffData.encryptedEmail, handoffData.encryptedPassword || "", companyCode);

                setLoginTransition("console-redirect");
                window.location.href = redirectUrl;
            } catch (error) {
                setError("Error redirecting. Please try again.");
            }
        } else if (choice === 'pos') {
            clearConsoleHandoffData();
            commitSelectedRole("EMPLOYEE");
            navigate("/pos-dashboard");
        }
    };

    const handleRoleChoice = (role) => {
        setShowRoleChoiceModal(false);

        const domainType = normalizeDomainType(localStorage.getItem("domainType"));
        if (domainType !== "FLEX_OFFICE") {
            setError("Role selection is only available for Flex Office.");
            return;
        }

        if (role === "CUSTOMER") {
            commitSelectedRole("CUSTOMER");
            sessionStorage.removeItem("guestAuthData");
            sessionStorage.removeItem("guestData");
            localStorage.removeItem("guestPrimaryId");
            sessionStorage.removeItem("guestshoppingCartId");
            sessionStorage.removeItem("encryptedPassword");
            sessionStorage.removeItem("encryptedEmail");
            sessionStorage.removeItem("guestAccountId");
            const pendingBooking = sessionStorage.getItem("pendingBooking");
            const loginRedirectUrl = sessionStorage.getItem("loginRedirectUrl");
            if (pendingBooking !== null) {
                sessionStorage.setItem("pendingBooking", pendingBooking);
            }

            if (loginRedirectUrl !== null) {
                sessionStorage.setItem("loginRedirectUrl", loginRedirectUrl);
            }

            if (pendingBooking) {
                try {
                    navigate("/selected-product-details", { replace: true, state: JSON.parse(pendingBooking) });
                } catch {
                    navigate("/flex-home", { replace: true });
                }
            } else {
                navigate("/flex-home", { replace: true });
            }
        } else if (["ADMIN", "SUPER_ADMIN", "INSTRUCTOR"].includes(role)) {
            const userEmail = localStorage.getItem("email");
            const userPassword = user?.password || "";

            performAdminConsoleRedirect(userEmail, userPassword);
        } else if (role === "EMPLOYEE") {
            setLoginTransition("employee-choice");
            setShowEmployeeChoiceModal(true);
        }
    };

    // Helper functions
    // Navigation
    const handleForgetPassword = () => navigate("/forgot-password");
    const handleForgetEmail = () => navigate("/forgot-email");

    return (
        <>
            {showEmployeeChoiceModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
                    <button
                        type="button"
                        aria-label="Close employee choice"
                        className="absolute inset-0 h-full w-full cursor-default bg-gray-900/40"
                        onClick={() => setShowEmployeeChoiceModal(false)}
                    />
                <div className="relative w-full max-w-sm rounded-2xl border border-gray-100 bg-white p-8 shadow-2xl fx-m-card">
                    <div className="text-center">
                        <div className="mb-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mx-auto flex items-center justify-center">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Welcome</h3>
                        <p className="text-gray-500 mb-6 text-sm">Choose your workspace</p>
                        <div className="space-y-3">
                            <button
                                type="button"
                                onClick={() => handleEmployeeChoice('console')}
                                className="w-full rounded-[10px] px-4 py-3 text-[15px] font-medium text-white shadow-lg transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                style={{ background: 'var(--flex-gradient, linear-gradient(135deg, #667eea 0%, #764ba2 100%))' }}
                            >
                                Console Dashboard
                            </button>
                            <button
                                type="button"
                                onClick={() => handleEmployeeChoice('pos')}
                                className="w-full rounded-[10px] px-4 py-3 text-[15px] font-medium text-white shadow-lg transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                style={{ background: 'linear-gradient(135deg, var(--flex-secondary, #f093fb) 0%, var(--flex-accent, #f5576c) 100%)' }}
                            >
                                POS System
                            </button>
                        </div>
                    </div>
                </div>
                </div>
            )}

            {showRoleChoiceModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
                    <button
                        type="button"
                        aria-label="Close role choice"
                        className="absolute inset-0 h-full w-full cursor-default bg-gray-900/40"
                        onClick={() => setShowRoleChoiceModal(false)}
                    />
                <div className="relative w-full max-w-sm rounded-2xl border border-gray-100 bg-white p-8 shadow-2xl fx-m-card">
                    <div className="mb-6 text-center">
                        <div
                            className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
                            style={{ background: 'var(--flex-gradient, linear-gradient(135deg, #6366f1 0%, #9333ea 100%))' }}
                        >
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Select Your Role</h2>
                        <p className="text-gray-500 text-sm mt-1">Choose how you want to continue</p>
                    </div>
                    <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="flex-role-choice">
                        Login Type
                    </label>
                    <select
                        id="flex-role-choice"
                        defaultValue=""
                        onChange={(e) => handleRoleChoice(e.target.value)}
                        className="h-12 w-full rounded-[10px] border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-[var(--flex-primary,#667eea)] focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="" disabled>Select login type</option>
                        {JSON.parse(localStorage.getItem("userRoles") || "[]").map((role) => (
                            <option key={role} value={role}>
                                {getFlexRoleLabel(role)}
                            </option>
                        ))}
                    </select>
                </div>
                </div>
            )}

            <div className="fx-m-hide"><br /> <br /></div>
            <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center py-8 px-4 fx-m-page">
                <div className="max-w-lg w-full fx-m-full-bleed">
                    <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                        <div className="p-10 fx-m-card">
                            {/* Header */}
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4 shadow-lg">
                                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h1>
                                <p className="text-gray-500">Sign in to continue to your account</p>
                            </div>

                            {/* Alerts */}
                            {state.open && (
                                <StatusMessage
                                    severity={state.severity}
                                    onClose={() => setState({ open: false, message: "", severity: "error" })}
                                    className="mb-3"
                                >
                                    {state.message}
                                </StatusMessage>
                            )}

                            {error && (
                                <StatusMessage severity="error" onClose={() => setError("")} className="mb-3">
                                    {error}
                                </StatusMessage>
                            )}

                            {success && (
                                <StatusMessage severity="success" onClose={() => setSuccess("")} className="mb-3">
                                    {success}
                                </StatusMessage>
                            )}

                            {/* Unified Login Form */}
                            <div className="space-y-6">
                                {/* Tabs for Email/Phone */}
                                <div className="mb-3 grid grid-cols-2 border-b border-gray-200">
                                    {["Email", "OTP"].map((label, index) => (
                                        <button
                                            type="button"
                                            key={label}
                                            onClick={() => {
                                                setActiveTab(index);
                                                setError("");
                                                setSuccess("");
                                                setOtpSent(false);
                                            }}
                                            className={`min-h-12 border-b-[3px] px-4 py-3 text-[15px] font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                                activeTab === index
                                                    ? "border-[var(--flex-primary,#3b82f6)] text-[var(--flex-primary,#3b82f6)]"
                                                    : "border-transparent text-gray-500 hover:text-gray-700"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {/* Email Login Form */}
                                {activeTab === 0 && (
                                    <form onSubmit={handleEmailLogin} className="space-y-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Email Address
                                            </label>
                                            <input
                                                id="login-email"
                                                name="email"
                                                type="email"
                                                value={user.email}
                                                onChange={handleEmailPasswordChange}
                                                required
                                                className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-700"
                                                placeholder="you@example.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Password
                                            </label>
                                            <div className="relative">
                                                <input
                                                    id="login-password"
                                                    name="password"
                                                    type={showPassword ? "text" : "password"}
                                                    value={user.password}
                                                    onChange={handleEmailPasswordChange}
                                                    required
                                                    className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 transition-all duration-200 text-gray-700"
                                                    placeholder="Enter your password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="h-5 w-5" aria-hidden="true" />
                                                    ) : (
                                                        <Eye className="h-5 w-5" aria-hidden="true" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center text-sm">
                                            <button
                                                type="button"
                                                onClick={handleForgetEmail}
                                                className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                                            >
                                                Forgot Email?
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleForgetPassword}
                                                className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                                            >
                                                Forgot Password?
                                            </button>
                                        </div>

                                        <button
                                            id="sign-in-button"
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                                        >
                                            {loading ? (
                                                <span className="flex items-center justify-center">
                                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                                                    Signing In...
                                                </span>
                                            ) : (
                                                "Sign In"
                                            )}
                                        </button>
                                    </form>
                                )}

                                {/* Phone OTP Login Form */}
                                {activeTab === 1 && (
                                    <form onSubmit={handleOtpLogin} className="space-y-5">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Phone Number
                                            </label>
                                            <div className="relative">
                                                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                                                    {countryCallingCode}
                                                </div>
                                                <input
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={handlePhoneChange}
                                                    disabled={otpSent}
                                                    required
                                                    className="w-full pl-16 pr-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 text-gray-700"
                                                    placeholder="Enter 10-digit number"
                                                />
                                            </div>
                                        </div>

                                        {!otpSent ? (
                                            <button
                                                type="button"
                                                onClick={handleSendOtp}
                                                disabled={loading || phoneNumber.length < 10}
                                                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                                            >
                                                {loading ? (
                                                    <span className="flex items-center justify-center">
                                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                                                        Sending OTP...
                                                    </span>
                                                ) : (
                                                    "Send OTP"
                                                )}
                                            </button>
                                        ) : (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Enter OTP
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={otp}
                                                        onChange={handleOtpChange}
                                                        required
                                                        className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest transition-all duration-200 text-gray-700 font-semibold"
                                                        placeholder="000000"
                                                        maxLength="6"
                                                    />
                                                    <div className="text-right mt-2">
                                                        {otpCountdown > 0 ? (
                                                            <span className="text-sm text-gray-500">
                                                                Resend OTP in <span className="font-semibold text-blue-600">{otpCountdown}s</span>
                                                            </span>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={handleSendOtp}
                                                                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                                                            >
                                                                Resend OTP
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <button
                                                    id="sign-in-button"
                                                    type="submit"
                                                    disabled={loading}
                                                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                                                >
                                                    {loading ? (
                                                        <span className="flex items-center justify-center">
                                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                                                            Verifying OTP...
                                                        </span>
                                                    ) : (
                                                        "Verify & Sign In"
                                                    )}
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setOtpSent(false);
                                                        setOtp("");
                                                        setPhoneNumber("");
                                                    }}
                                                    className="w-full text-blue-600 hover:text-blue-700 font-medium py-2 text-sm transition-colors hover:underline"
                                                >
                                                    ← Use different phone number
                                                </button>
                                            </>
                                        )}
                                    </form>
                                )}
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-4 bg-white text-gray-500 font-medium flex-office-login-divider-label">Or continue with</span>
                                    </div>
                                </div>
                                {/* Google Sign In */}
                                <div className="flex justify-center">
                                    {isNativeMobileApp() ? (
                                        <GoogleAuthButton
                                            onClick={handleNativeGoogleSignIn}
                                            disabled={loading}
                                            className="flex-office-google-shell"
                                        />
                                    ) : (
                                        <div className="w-full rounded-xl bg-white shadow-sm flex-office-google-shell">
                                            <div
                                                id="googleOneTapButton"
                                                className="flex w-full items-center justify-center overflow-hidden rounded-xl"
                                            ></div>
                                        </div>
                                    )}
                                </div>


                            </div>

                            {/* Footer */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <div className="space-y-4">
                                    <p className="text-xs text-gray-500 text-center leading-relaxed">
                                        By continuing, you acknowledge that you have read and agree to our{" "}
                                        {legalDocumentLinks.length > 0 ? (
                                            legalDocumentLinks.map((doc, index, array) => (
                                                <span key={index}>
                                                    <a
                                                        href={doc.href}
                                                        onClick={(e) => handleLegalDocumentClick(doc.document, e)}
                                                        className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline cursor-pointer"
                                                    >
                                                        {doc.name}
                                                    </a>
                                                    {index < array.length - 2 ? ", " : index === array.length - 2 ? " and " : ""}
                                                </span>
                                            ))
                                        ) : (
                                            <>
                                                <a href="/terms" className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline">
                                                    Terms of Service
                                                </a>{" "}
                                                and{" "}
                                                <a href="/privacy" className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline">
                                                    Privacy Policy
                                                </a>
                                            </>
                                        )}
                                        .
                                    </p>

                                    <p className="text-sm text-gray-600 text-center">
                                        Need an account?{" "}
                                        <button
                                            onClick={() => navigate("/sign-up")}
                                            className="text-blue-600 hover:text-blue-700 font-semibold transition-colors hover:underline"
                                        >
                                            Sign up here
                                        </button>
                                    </p>
                                </div>
                            </div>
                            {showContinueAsModal && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
                                    <button
                                        type="button"
                                        aria-label="Close continue as dialog"
                                        className="absolute inset-0 h-full w-full cursor-default bg-gray-900/40"
                                        onClick={() => setShowContinueAsModal(false)}
                                    />
                                    <section className="relative w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
                                        <h3 className="text-xl font-semibold text-gray-900">
                                            Welcome back, {localStorage.getItem("email")}
                                        </h3>
                                        <p className="mt-3 text-sm text-gray-600">
                                            You were previously logged in. How would you like to continue?
                                        </p>
                                        <div className="mt-6 space-y-3">
                                            <button
                                                type="button"
                                                onClick={() => handleRoleChoice("CUSTOMER")}
                                                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                            >
                                                Continue as User
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRoleChoice("ADMIN")}
                                                className="w-full rounded-lg bg-slate-800 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                                            >
                                                Continue as Admin
                                            </button>
                                            <button
                                                type="button"
                                                onClick={async () => { await clearSessionButKeepTheme(); setShowContinueAsModal(false); }}
                                                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                Start fresh / Login differently
                                            </button>
                                        </div>
                                    </section>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

function mapStateToProps(state) {
    const { isAuthenticated, user } = state?.auth?.data || {};
    return { isAuthenticated: isAuthenticated, user: user };
}

export default connect(mapStateToProps, { signin, googleSignin, sendOtp, OtpLogin })(FlexLogin);

